import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { getChatAnalytics } from '../../services/chatApi';
import './AIChatAnalyticsPage.css';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

const INTENT_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#ec4899', '#14b8a6', '#6366f1',
];

const DAY_OPTIONS = [7, 30, 90];

function formatMs(ms) {
  if (!ms) return '0 ms';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function formatMinutes(min) {
  if (min < 60) return `${min} min`;
  const h = (min / 60).toFixed(1);
  return `${h} hr`;
}

function KPICard({ label, value, sub }) {
  return (
    <div className="ai-kpi-card">
      <div className="ai-kpi-value">{value}</div>
      <div className="ai-kpi-label">{label}</div>
      {sub && <div className="ai-kpi-sub">{sub}</div>}
    </div>
  );
}

export default function AIChatAnalyticsPage() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getChatAnalytics(days);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const kpis = data?.kpis;

  const dailyChartData = {
    labels: data?.daily_trend.map((d) => d.date) ?? [],
    datasets: [
      {
        label: 'Conversations',
        data: data?.daily_trend.map((d) => d.count) ?? [],
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59,130,246,0.12)',
        tension: 0.4,
        fill: true,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  const dailyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { maxTicksLimit: 8 } },
      y: { beginAtZero: true, ticks: { stepSize: 1 } },
    },
  };

  const intentLabels = data?.intent_breakdown.map((i) => i.intent) ?? [];
  const intentValues = data?.intent_breakdown.map((i) => i.count) ?? [];
  const intentChartData = {
    labels: intentLabels,
    datasets: [
      {
        data: intentValues,
        backgroundColor: INTENT_COLORS.slice(0, intentLabels.length),
        borderWidth: 0,
      },
    ],
  };
  const intentChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'right', labels: { boxWidth: 12, padding: 10 } },
    },
    cutout: '65%',
  };

  const toolLabels = data?.top_tools.map((t) => t.tool) ?? [];
  const toolValues = data?.top_tools.map((t) => t.count) ?? [];
  const toolChartData = {
    labels: toolLabels,
    datasets: [
      {
        data: toolValues,
        backgroundColor: '#3b82f6',
        borderRadius: 4,
      },
    ],
  };
  const toolChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, ticks: { stepSize: 1 } },
      y: { grid: { display: false } },
    },
  };

  return (
    <div className="ai-analytics-page">
      <div className="ai-analytics-header">
        <button className="ai-analytics-back" onClick={() => navigate('/settings')}>
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Settings
        </button>
        <div className="ai-analytics-title-row">
          <div>
            <h1>AI Chat Analytics</h1>
            <p>Usage, cost, and performance insights for the AI assistant</p>
          </div>
          <div className="ai-analytics-day-selector">
            {DAY_OPTIONS.map((d) => (
              <button
                key={d}
                className={`day-btn${days === d ? ' active' : ''}`}
                onClick={() => setDays(d)}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="ai-analytics-error">
          Failed to load analytics: {error}
        </div>
      )}

      {loading ? (
        <div className="ai-analytics-loading">Loading analytics...</div>
      ) : (
        <>
          {/* KPI Strip */}
          <div className="ai-kpi-strip">
            <KPICard
              label="Total Conversations"
              value={kpis?.total_conversations?.toLocaleString() ?? '0'}
            />
            <KPICard
              label="Time Saved"
              value={formatMinutes(kpis?.total_time_saved_minutes ?? 0)}
              sub="est. 5 min per session"
            />
            <KPICard
              label="Estimated Cost"
              value={`$${(kpis?.total_cost_usd ?? 0).toFixed(4)}`}
              sub="Claude Haiku 4.5"
            />
            <KPICard
              label="Success Rate"
              value={`${((kpis?.success_rate ?? 0) * 100).toFixed(1)}%`}
            />
            <KPICard
              label="Avg Response Time"
              value={formatMs(kpis?.avg_response_time_ms ?? 0)}
            />
          </div>

          {/* Charts row */}
          <div className="ai-charts-row">
            <div className="ai-chart-card ai-chart-card--wide">
              <h3>Daily Usage</h3>
              <div className="ai-chart-body">
                {(data?.daily_trend?.length ?? 0) === 0
                  ? <EmptyState text="No conversations yet" />
                  : <Line data={dailyChartData} options={dailyChartOptions} />
                }
              </div>
            </div>

            <div className="ai-chart-card">
              <h3>Intent Breakdown</h3>
              <div className="ai-chart-body">
                {(data?.intent_breakdown?.length ?? 0) === 0
                  ? <EmptyState text="No intent data" />
                  : <Doughnut data={intentChartData} options={intentChartOptions} />
                }
              </div>
            </div>
          </div>

          {/* Tools chart */}
          {(data?.top_tools?.length ?? 0) > 0 && (
            <div className="ai-chart-card ai-chart-card--full">
              <h3>Top Tools Called</h3>
              <div className="ai-chart-body ai-chart-body--tools">
                <Bar data={toolChartData} options={toolChartOptions} />
              </div>
            </div>
          )}

          {/* Recent conversations table */}
          <div className="ai-recent-card">
            <h3>Recent Conversations</h3>
            {(data?.recent_conversations?.length ?? 0) === 0 ? (
              <EmptyState text="No recent conversations" />
            ) : (
              <div className="ai-recent-table-wrap">
                <table className="ai-recent-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Question</th>
                      <th>Intent</th>
                      <th>Tokens In / Out</th>
                      <th>Response Time</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_conversations.map((conv) => (
                      <tr key={conv.id}>
                        <td className="ai-col-date">
                          {new Date(conv.created_at).toLocaleDateString()}
                        </td>
                        <td className="ai-col-question" title={conv.question}>
                          {conv.question.length > 80
                            ? conv.question.slice(0, 80) + '…'
                            : conv.question}
                        </td>
                        <td>
                          {conv.intent
                            ? <span className="ai-intent-badge">{conv.intent}</span>
                            : <span className="ai-intent-badge ai-intent-badge--unknown">—</span>
                          }
                        </td>
                        <td className="ai-col-mono">
                          {conv.input_tokens.toLocaleString()} / {conv.output_tokens.toLocaleString()}
                        </td>
                        <td className="ai-col-mono">{formatMs(conv.response_time_ms)}</td>
                        <td>
                          <span className={`ai-status-badge ai-status-badge--${conv.was_successful ? 'ok' : 'err'}`}>
                            {conv.was_successful ? 'OK' : 'Error'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="ai-empty-state">{text}</div>
  );
}
